import { useState } from 'react'
import { useOutletContext } from 'react-router-dom'

import { Box, Form } from '@factorialco/deprecated-design-system'
import { RichTextEditor } from '@factorialco/f0-react/dist/experimental'
import {
  AtsCandidateFeedbacksDocument,
  AtsCreateFeedbackNewDocument,
  AtsFeedbackNewApplicationFragment,
  AtsJobApplicationNotesDocument,
  GetAtsFeedbacksDocument,
} from 'generated/resources/graphql'
import { AtsFeedbackNewFeaturesDocument } from 'generated/resources/graphql'

import UserAvatar from 'modules/core/components/UserAvatar'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { RatingSelector } from 'modules/ats/components/Feedbacks/RatingSelector'
import { VisibilityToggler } from 'modules/ats/components/Feedbacks/VisibilityToggler'
import { AtsFeedbackFormValues } from 'modules/ats/lib/utils/feedbacks'
import { useRichTextEditorNotesMentions } from 'modules/ats/lib/utils/useNotesMentions'

interface Props {
  candidateId: string
  application?: AtsFeedbackNewApplicationFragment
  draftNote: string | undefined | null
  onDraftChange: (draft: string) => void
  closeNote: () => void
  enabledScoring?: boolean
  allowRestrictedComments?: boolean
}

interface AtsFeedbackCreateFormValues extends AtsFeedbackFormValues {
  accessId: string
}

interface OutletContext {
  isReviewerRole: boolean
}

export const FeedbackNew = ({
  application,
  candidateId,
  closeNote,
  draftNote,
  onDraftChange,
  allowRestrictedComments = true,
  enabledScoring = true,
}: Props) => {
  const { editorConfig } = useRichTextEditor()
  const { data: featuresData } = useQuery(AtsFeedbackNewFeaturesDocument)
  const { isReviewerRole } = useOutletContext<OutletContext>()

  const restrictedCommentsAvailable =
    featuresData?.companies.featureAvailabilitiesConnection.nodes.find(
      (node) => node.id === 'ats_customized_features'
    )?.available

  const [createFeedback, { loading }] = useMutation(AtsCreateFeedbackNewDocument, {
    extractorFn: (data) => data.atsMutations.createAtsFeedback,
    refetchQueries: [
      GetAtsFeedbacksDocument,
      AtsJobApplicationNotesDocument,
      AtsCandidateFeedbacksDocument,
    ],
  })
  const [accessesIds, setAccessesIds] = useState<string[] | null>([])

  const {
    accesses: uniqueRichTextEditorMentioningAccesses,
    loading: richTextEditorMentionsLoading,
  } = useRichTextEditorNotesMentions({
    atsJobPostingId: application?.jobPosting.id,
    candidateId,
  })

  const { access } = useCurrentSession()
  const form = useForm<AtsFeedbackCreateFormValues>({
    defaultValues: {
      accessId: access.id,
      description: draftNote || '',
      rating: null,
      atsCandidateId: candidateId,
      atsApplicationId: application?.id,
      atsApplicationPhaseId: application?.applicationPhase.id,
      attachments: [],
      mentionIds: accessesIds,
      visibility: 'public',
    },
    async onSubmit(data, { setErrors }) {
      const result = await createFeedback({
        variables: {
          ...data,
          attachments: data.attachments,
          mentionIds: accessesIds,
        },
      })

      const handled = handleResult(result, setErrors)

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })

        onDraftChange('')
        setAccessesIds(null)
        form.resetForm()
        closeNote()
      }

      return handled
    },
  })

  // TODO: Make files updates & delete work
  const onFiles = useUnsafeCallback(
    (attachments) => {
      form.setFieldValue('attachments', attachments)
    },
    [form]
  )

  const restrictedCommentsEnabled =
    restrictedCommentsAvailable && !isReviewerRole && allowRestrictedComments

  return (
    <Form padding={null}>
      <Box flexDirection="row" gap="s32" justifyContent="spaceBetween" alignItems="center">
        <Box gap="s8" flexDirection="row" alignItems="center" maxWidth="s600">
          <UserAvatar user={access} size="s40" />
          {enabledScoring && (
            <RatingSelector
              value={Number(form.values.rating)}
              onChange={(rating) => form.setFieldValue('rating', rating)}
            />
          )}
        </Box>
        {restrictedCommentsEnabled && (
          <VisibilityToggler
            value={form.values.visibility}
            onChange={(value) => form.setFieldValue('visibility', value)}
          />
        )}
      </Box>

      {richTextEditorMentionsLoading ? (
        <RichTextEditor.Skeleton rows={1} />
      ) : (
        <RichTextEditor
          {...editorConfig}
          title={i18n.t('ats.feedbacks.title_new')}
          onChange={(data) => {
            form.setFieldValue('description', data?.value)
            setAccessesIds(data?.mentionIds?.map(String) ?? null)
            if (data?.value) {
              onDraftChange(data?.value)
            }
          }}
          placeholder={i18n.t('ats.mentions.placeholder')}
          filesConfig={{
            onFiles: onFiles,
            multipleFiles: true,
          }}
          mentionsConfig={{
            users: uniqueRichTextEditorMentioningAccesses,
          }}
          primaryAction={{
            action: {
              label: i18n.t('common.save'),
              onClick: () => form.handleSubmit(),
              variant: 'default',
              disabled: loading,
            },
          }}
          initialEditorState={{
            content: form.values.description || '',
          }}
          secondaryAction={[
            {
              label: i18n.t('common.cancel'),
              onClick: () => closeNote(),
              variant: 'outline',
              type: 'button',
              disabled: loading,
            },
          ]}
        />
      )}
    </Form>
  )
}
